import { useCallback, useState } from "react";
import {
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
} from "@chakra-ui/react";
import { gql } from "__generated__/gql";
import { useBrandQuery } from "utils/apollo/hooks/brand";
import { useCurrentBrandId } from "utils/currentUserHooks";
import CreateManufacturingFacilityForm from "./CreateForm";
import EditManufacturingFacilityForm from "./EditForm";
import ManufacturingFacilitiesList from "./List";

const facilitiesQuery = gql(/* GraphQL */ `
  query ProductLinePanelManufacturingFacilities($brandId: UUID!) {
    manufacturingFacilitiesList(
      condition: { brandId: $brandId }
      orderBy: NAME_ASC
    ) {
      id
      ...ProductLinePanelManufacturingFacilityListItem
      ...ProductLinePanelEditManufacturingFacility
    }
  }
`);

const MANUFACTURING_FACILITY_FORM_ID = "manage-manufacturing-facility-form";

enum ManageManufacturingFacilitiesMode {
  List = "list",
  Create = "create",
  Edit = "edit",
}

export default function ManageManufacturingFacilitiesModalContent() {
  const [mode, setMode] = useState<ManageManufacturingFacilitiesMode>(
    ManageManufacturingFacilitiesMode.List,
  );
  const [editingFacilityId, setEditingFacilityId] = useState<UUID | null>(null);
  const brandId = useCurrentBrandId();

  const { data, loading: isLoadingFacilities } = useBrandQuery(
    facilitiesQuery,
    {
      skip: !brandId,
      variables: brandId ? { brandId } : undefined,
    },
  );

  const facilities = data?.manufacturingFacilitiesList;

  const returnToListMode = useCallback(() => {
    setMode(ManageManufacturingFacilitiesMode.List);
    setEditingFacilityId(null);
  }, []);

  return (
    <ModalContent>
      <ModalHeader>Manage Manufacturing Facilities</ModalHeader>

      <ModalCloseButton />

      {mode === ManageManufacturingFacilitiesMode.List && (
        <ModalBody pb={6}>
          <ManufacturingFacilitiesList
            facilities={facilities}
            isLoading={isLoadingFacilities}
            onAdd={() => {
              setMode(ManageManufacturingFacilitiesMode.Create);
            }}
            onEdit={(facilityId) => {
              setEditingFacilityId(facilityId);
              setMode(ManageManufacturingFacilitiesMode.Edit);
            }}
          />
        </ModalBody>
      )}

      {mode === ManageManufacturingFacilitiesMode.Create && (
        <CreateManufacturingFacilityForm
          formId={MANUFACTURING_FACILITY_FORM_ID}
          isLoading={isLoadingFacilities}
          onCancel={returnToListMode}
          onCreated={returnToListMode}
        />
      )}

      {mode === ManageManufacturingFacilitiesMode.Edit && (
        <EditManufacturingFacilityForm
          facility={facilities?.find(
            (facility) => facility.id === editingFacilityId,
          )}
          formId={MANUFACTURING_FACILITY_FORM_ID}
          isLoading={isLoadingFacilities}
          onCancel={returnToListMode}
          onUpdated={returnToListMode}
        />
      )}
    </ModalContent>
  );
}
